/**
 * `usePhotos(estimateId)` — fetches photos and polls every 5 s while
 * any photo is in `pending` or `processing` (FR-X3).
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { listPhotos } from "../api/sdk.gen";
import type { Photo } from "../api/types.gen";

const POLL_INTERVAL_MS = 5000;

interface UsePhotosResult {
  photos: Photo[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Optimistically prepend a freshly-uploaded photo. */
  prepend: (p: Photo) => void;
  remove: (photoId: string) => void;
}

function hasInFlight(photos: Photo[]): boolean {
  return photos.some(
    (p) => p.dimension_status === "pending" || p.dimension_status === "processing",
  );
}

export function usePhotos(estimateId: string | undefined): UsePhotosResult {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!estimateId) return;
    const result = await listPhotos({ estimateId });
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    setError(null);
    setPhotos(result.data);
  }, [estimateId]);

  // Initial fetch.
  useEffect(() => {
    if (!estimateId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      await fetchOnce();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId, fetchOnce]);

  // Background polling while anything is in flight.
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!hasInFlight(photos)) return;
    timerRef.current = setTimeout(() => {
      void fetchOnce();
    }, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [photos, fetchOnce]);

  const prepend = useCallback((p: Photo) => {
    setPhotos((prev) => [p, ...prev]);
  }, []);

  const remove = useCallback((photoId: string) => {
    setPhotos((prev) => prev.filter((p) => p.photo_id !== photoId));
  }, []);

  return { photos, loading, error, refresh: fetchOnce, prepend, remove };
}
